import type { FC } from "react";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { usePopover } from "hooks/use-popover";
import { FolderItem } from "../interface";
import { popoverMenuItems } from "../MyTemplateHelper";
import strings from "global/constants/StringConstants";
import { convertESTtoUserLocalDateAndTime, isTruthy } from "helpers/methods";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { setIsRequest } from "redux/appSlice";
import { SignRequest } from "models/interfaces";
import { useFeature, useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import templatesStyles from "../Templates.styles";
import templatesTokens, { templatesTypeTokens } from "../Templates.tokens";
import {
  DocumentSmallGlyph,
  FolderSmallGlyph,
  KebabGlyph,
} from "./TemplatesIcons";
import LibrarySourceBadges, {
  isLibraryKit,
  libraryCardSx,
  librarySubLabel,
} from "../TemplateLibrary/components/LibrarySourceBadges";
import { KitGlyph } from "../TemplateLibrary/TemplateLibraryIcons";

const styles = templatesStyles;
const { row, kebab } = templatesTokens;

interface ItemListRowProps {
  item: FolderItem;
  setOpenDialog: (value: boolean) => void;
  setDialogType: (value: string) => void;
  selectedFolder: FolderItem | null;
  setSelectedFolder: (value: FolderItem | null) => void;
  setNewFolderName: (value: string) => void;
  setFolderNameError: (value: string) => void;
  handleFolderClick: (item: FolderItem) => void;
  handleUpdateTemplate: () => void;
  handleUseTemplate: (item?: SignRequest) => void;
  handleCreateWebForm?: () => void;
  isLoading?: boolean;
  handleCloseTemplateModal: any;
  dispatch: any;
  isUseTemplate?: boolean;
  isShowMoreIcon?: boolean;
  isMobile?: boolean;
}

/**
 * One row of the Templates list view. Shares the header's five-column grid
 * (name | type | created by | created | kebab) so the columns line up down the
 * card; below `md` the type, created-by and created columns fold away and the
 * created stamp moves under the name.
 */
export const ItemListRow: FC<ItemListRowProps> = (props) => {
  const { item } = props;
  const popover = usePopover<HTMLElement>();
  const webFormsAvailable = useFeature(FeatureKeys.ENVELOPE_WEB_FORMS);
  const webFormsState = useFeatureState(FeatureKeys.ENVELOPE_WEB_FORMS);
  const webFormsTooltip = webFormsAvailable
    ? ""
    : webFormsState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((webFormsState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : strings.FEATURE_GATE_UPSELL_TOOLTIP;

  const isFolder = item.type === strings.folder;
  const isKit = isLibraryKit(item);
  const tint =
    isFolder || isKit
      ? templatesTypeTokens.folder
      : templatesTypeTokens.template;
  const itemCount = item.itemCount ?? 0;
  const subLabel = isFolder
    ? `${itemCount} ${itemCount === 1 ? "item" : "items"}`
    : (librarySubLabel(item) ?? "Document template");

  const createdAt =
    item.createdOn &&
    convertESTtoUserLocalDateAndTime(
      item.createdOn,
      "MMMM D, YYYY h:mm A",
      "MM/DD/YYYY HH:mm:ss",
    );

  const createdBy = item.requestedBy?.name;

  const folderMenuFunction = (label: string) => {
    props.setDialogType(label);
    if (label === strings.rename && props.selectedFolder) {
      props.setNewFolderName(props.selectedFolder.templateName ?? "");
    } else if (label === strings.update) {
      props.handleUpdateTemplate();
    } else if (label === strings.use) {
      props.handleUseTemplate();
    } else if (label === "Web Form") {
      props.handleCreateWebForm?.();
      return;
    } else {
      props.setNewFolderName("");
    }
    props.setFolderNameError("");
    props.setOpenDialog(true);
  };

  const isAnyMenuItemAvailable =
    popoverMenuItems(props.selectedFolder?.type!, webFormsAvailable)?.length > 0;
  const showKebab = (props.isShowMoreIcon ?? true) && isAnyMenuItemAvailable;

  const openMenuAt = (anchor: HTMLElement) => {
    popover.anchorRef.current = anchor;
    popover.handleOpen();
    props.setSelectedFolder?.(item);
  };

  const handleRowClick = () => {
    props.setSelectedFolder?.(item);
    if (!props.isLoading && isFolder) {
      props.handleFolderClick(item);
      return;
    }
    if (item.type) {
      const currentMenuItems = popoverMenuItems(item.type, webFormsAvailable);
      if (!currentMenuItems.includes(strings.use)) return;
    }
    if (!props.isUseTemplate) return;
    if (!props.isLoading && item.type === strings.template) {
      props.handleUseTemplate(item);
      return;
    }
    props.dispatch(setIsRequest(true));
    history.push({
      pathname: viewpaths.useTemplateViewPath,
      state: item,
    });
    props.handleCloseTemplateModal?.();
  };

  return (
    <>
      <Box
        sx={[styles.listRow(!!props.isLoading), libraryCardSx(item)] as any}
        onClick={handleRowClick}
        onContextMenu={(event) => {
          event.preventDefault();
          if (props.isLoading || !showKebab) return;
          const anchor = document.createElement("div");
          anchor.style.position = "fixed";
          anchor.style.top = `${event.clientY}px`;
          anchor.style.left = `${event.clientX}px`;
          document.body.appendChild(anchor);
          openMenuAt(anchor);
        }}
      >
        <Box sx={styles.rowLead}>
          <Box sx={styles.rowIconTile(tint.bg)}>
            {isFolder ? (
              <FolderSmallGlyph
                sx={{
                  width: row.folderIconSize,
                  height: row.folderIconSize,
                  color: tint.stroke,
                }}
              />
            ) : isKit ? (
              <KitGlyph
                sx={{
                  width: row.docIconSize,
                  height: row.docIconSize,
                  color: tint.stroke,
                }}
              />
            ) : (
              <DocumentSmallGlyph
                sx={{
                  width: row.docIconSize,
                  height: row.docIconSize,
                  color: tint.stroke,
                }}
              />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Tooltip
              title={item.templateName ?? ""}
              disableHoverListener={(item.templateName ?? "").length <= 30}
              arrow
              describeChild
            >
              <Typography
                component="button"
                type="button"
                aria-label={`Open ${item.templateName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleRowClick();
                }}
                sx={styles.rowNameButton}
              >
                {item.templateName}
              </Typography>
            </Tooltip>
            <LibrarySourceBadges item={item} sx={{ my: "4px" }} />
            <Typography component="div" sx={styles.rowSub}>
              {subLabel}
              {/*
                Below `md` the type / created-by / created columns fold away,
                so the reference folds the stamp back onto the meta line rather
                than dropping it.
              */}
              {isTruthy(createdAt) && (
                <Box
                  component="span"
                  sx={{ display: { xs: "inline", md: "none" } }}
                >
                  {` · ${createdAt}`}
                </Box>
              )}
            </Typography>
          </Box>
        </Box>

        <Typography component="div" sx={styles.rowType}>
          {isFolder ? "Folder" : "Template"}
        </Typography>
        <Typography component="div" sx={styles.rowCreatedBy}>
          {isTruthy(createdBy) ? createdBy : "—"}
        </Typography>
        <Typography component="div" sx={styles.rowCreated}>
          {isTruthy(createdAt) ? createdAt : ""}
        </Typography>

        <Box sx={styles.kebabColumn}>
          {showKebab && (
            <IconButton
              sx={styles.kebabButton}
              disabled={props.isLoading}
              aria-label="More options"
              onClick={(event) => {
                if (props.isLoading) return;
                event.stopPropagation();
                openMenuAt(event.currentTarget);
              }}
              ref={
                popover.anchorRef as React.MutableRefObject<HTMLButtonElement | null>
              }
            >
              <KebabGlyph
                sx={{ width: kebab.iconSize, height: kebab.iconSize }}
              />
            </IconButton>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        open={popover.open}
        onClick={(event) => event.stopPropagation()}
        onClose={() => {
          popover.handleClose();
          if (
            popover.anchorRef.current &&
            popover.anchorRef.current.parentNode === document.body
          ) {
            document.body.removeChild(popover.anchorRef.current);
          }
        }}
      >
        {popoverMenuItems(props.selectedFolder?.type!, webFormsAvailable).map(
          (label) => {
            const isWebFormLocked = label === "Web Form" && !webFormsAvailable;
            const menuItem = (
              <MenuItem
                key={label}
                disabled={isWebFormLocked}
                onClick={() => {
                  if (isWebFormLocked) return;
                  popover.handleClose();
                  folderMenuFunction(label);
                }}
              >
                {label}
                {isWebFormLocked && (
                  <LockOutlinedIcon
                    sx={{
                      fontSize: kebab.iconSize,
                      color: "text.secondary",
                      ml: "auto",
                    }}
                  />
                )}
              </MenuItem>
            );
            return isWebFormLocked ? (
              <Tooltip
                key={label}
                title={webFormsTooltip}
                placement="left"
                arrow
                describeChild
              >
                <Box component="span" sx={{ display: "block" }}>
                  {menuItem}
                </Box>
              </Tooltip>
            ) : (
              menuItem
            );
          },
        )}
      </Menu>
    </>
  );
};
